import { useRef, useEffect } from 'react';
import { parseMarkdown } from '../lib/markdown';

interface ResponseAreaProps {
    response: string | null;
    isLoading: boolean;
    userQuestion?: string | null;
}

/**
 * ResponseArea - Displays loading dots or markdown response
 * Auto-scrolls during streaming for smooth experience
 */
export default function ResponseArea({
    response,
    isLoading,
    userQuestion,
}: ResponseAreaProps) {
    // Show response container as soon as we have content (streaming or complete)
    const hasContent = response !== null && response.length > 0;
    // Show loading dots only when loading AND no content yet (before streaming starts)
    const showLoading = isLoading && !hasContent;
    // Show question when we have a question (during or after loading)
    const showQuestion = userQuestion && (isLoading || hasContent);

    // Ref for auto-scroll
    const containerRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when response updates during streaming
    useEffect(() => {
        if (hasContent && isLoading && containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }, [response, hasContent, isLoading]);

    return (
        <>
            {/* User Question Display */}
            {showQuestion && (
                <div className="user-question-container">
                    <div className="user-question-label">
                        <i className="fas fa-user"></i>
                        <span>Sua pergunta</span>
                    </div>
                    <div className="user-question-text">{userQuestion}</div>
                </div>
            )}

            {/* Loading Indicator */}
            <div
                id="loadingIndicator"
                className={`loading-indicator ${showLoading ? 'visible' : ''}`}
            >
                <div className="loading-dot"></div>
                <div className="loading-dot"></div>
                <div className="loading-dot"></div>
            </div>

            {/* Response Area - direct scroll, no wrapper */}
            <div
                id="responseContainer"
                ref={containerRef}
                className={`response-container ${hasContent ? 'visible' : ''}`}
            >
                <div className="response-title">
                    <i className="fas fa-crystal-ball"></i>
                    <span>Resposta do Oráculo</span>
                </div>
                <div
                    id="responseText"
                    className="response-text"
                    dangerouslySetInnerHTML={{
                        __html: response ? parseMarkdown(response) : '',
                    }}
                />
            </div>
        </>
    );
}
